import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Breadcrumbs,
  Link as MuiLink,
  Button,
  TextField,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import DataAdminGrid from "../../components/dataAdminGrid/DataAdminGrid";
import DataGridInfoAlert from "../../components/dataAdminGrid/InfoAlert";
import {
  PERMITS_BASE_URL,
  PERMIT_PK_FIELD,
  COLUMNS as BASE_COLUMNS,
} from "./permitsConfig";
import { ROUTES } from "../../constants";

import AutocompleteEditCell from "../../components/dataAdminGrid/AutocompleteEditCell";
import ColumnsPopover from "../../components/dataAdminGrid/ColumnsPopover";
import AllocationProfileCellWithView from "./AllocationProfileCellWithView";
import AllocationProfileAutocompleteEditCell from "./AllocationProfileAutocompleteEditCell";
import { usePermitLookups } from "./usePermitLookups";

function PermitsPage() {
  const { contacts, wells, droughtZones, allocationProfiles } =
    usePermitLookups();

  const [showInactiveOnly, setShowInactiveOnly] = useState(false);
  const [searchText, setSearchText] = useState("");

  const contactsById = useMemo(() => {
    const map = new Map();
    for (const contact of contacts) {
      map.set(contact.contact_ndx, contact);
    }
    return map;
  }, [contacts]);

  const wellsById = useMemo(() => {
    const map = new Map();
    for (const well of wells) {
      map.set(well.well_ndx, well);
    }
    return map;
  }, [wells]);

  const droughtZonesById = useMemo(() => {
    const map = new Map();
    for (const zone of droughtZones) {
      map.set(zone.zone_ndx, zone);
    }
    return map;
  }, [droughtZones]);

  const allocationProfilesById = useMemo(() => {
    const map = new Map();
    for (const profile of allocationProfiles) {
      map.set(profile.profile_ndx, profile);
    }
    return map;
  }, [allocationProfiles]);

  const normalizedNeedle = searchText.trim().toLowerCase();

  const rowMatchesSearch = useMemo(() => {
    if (!normalizedNeedle) return () => true;

    return (row) => {
      if (row?.permit_ndx == null) return true;

      const contactName = contactsById.get(row?.assoc_contact_ndx)?.joined_name;
      const droughtZoneDesc = droughtZonesById.get(
        row?.assoc_drought_zone_ndx
      )?.zone_desc;
      const allocationProfileName = allocationProfilesById.get(
        row?.allocation_profile_ndx
      )?.profile_name;

      const wellIds = Array.isArray(row?.assoc_well_ndx)
        ? row.assoc_well_ndx
        : [];
      const wellLabels = wellIds
        .map((id) => wellsById.get(id)?.well_ndx ?? id)
        .filter((x) => x != null);

      const searchable = [
        row?.permit_ndx,
        row?.permit_nickname,
        row?.assoc_contact_ndx,
        contactName,
        row?.assoc_drought_zone_ndx,
        droughtZoneDesc,
        row?.allocation_profile_ndx,
        allocationProfileName,
        row?.id_permit_legacy,
        row?.permit_acre_feet,
        row?.permit_gpy,
        row?.dt_permitted,
        row?.dt_expiration,
        row?.req_wl_reporting,
        row?.is_active,
        row?.has_connections,
        ...wellLabels,
      ]
        .filter((v) => v !== undefined && v !== null)
        .join(" ")
        .toLowerCase();

      return searchable.includes(normalizedNeedle);
    };
  }, [
    normalizedNeedle,
    contactsById,
    droughtZonesById,
    allocationProfilesById,
    wellsById,
  ]);

  const columns = useMemo(() => {
    return BASE_COLUMNS.map((col) => {
      if (col.field === "assoc_contact_ndx") {
        return {
          ...col,
          renderCell: ({ value }) => {
            const found = contacts.find((c) => c.contact_ndx === value);
            return found ? (
              found.joined_name
            ) : (
              <Typography
                sx={{
                  fontStyle: "italic",
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                No Contact Selected
              </Typography>
            );
          },
          renderEditCell: (params) => (
            <AutocompleteEditCell
              {...params}
              options={contacts}
              optionIdField="contact_ndx"
              optionLabelField="joined_name"
              multiple={false}
              label="Contact"
            />
          ),
        };
      }

      if (col.field === "assoc_well_ndx") {
        return {
          ...col,
          renderCell: ({ value }) => {
            const wellIds = Array.isArray(value) ? value : [];
            if (!wellIds.length) {
              return (
                <Typography
                  sx={{
                    fontStyle: "italic",
                    color: "text.secondary",
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  No Wells Selected
                </Typography>
              );
            }

            const wellLabels = wellIds.map((id) => {
              const w = wells.find((x) => x.well_ndx === id);
              return w ? w.well_ndx || `Well #${id}` : `Well #${id}`;
            });

            return <ColumnsPopover name="Wells" items={wellLabels} />;
          },
          renderEditCell: (params) => (
            <AutocompleteEditCell
              {...params}
              options={wells}
              optionIdField="well_ndx"
              optionLabelField="well_ndx"
              multiple={true}
              label="Wells"
            />
          ),
        };
      }

      if (col.field === "assoc_drought_zone_ndx") {
        return {
          ...col,
          renderCell: ({ value }) => {
            const found = droughtZones.find((z) => z.zone_ndx === value);
            return found ? (
              found.zone_desc
            ) : (
              <Typography
                sx={{
                  fontStyle: "italic",
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                No Drought Zone Selected
              </Typography>
            );
          },
          renderEditCell: (params) => (
            <AutocompleteEditCell
              {...params}
              options={droughtZones}
              optionIdField="zone_ndx"
              optionLabelField="zone_desc"
              multiple={false}
              label="Drought Zone"
            />
          ),
        };
      }

      if (col.field === "allocation_profile_ndx") {
        return {
          ...col,
          renderCell: ({ value }) => (
            <AllocationProfileCellWithView
              value={value}
              allocationProfiles={allocationProfiles}
            />
          ),
          renderEditCell: (params) => (
            <AllocationProfileAutocompleteEditCell
              {...params}
              options={allocationProfiles}
              optionIdField="profile_ndx"
              optionLabelField="profile_name"
              multiple={false}
              label="Allocation Profile"
            />
          ),
        };
      }

      return col;
    });
  }, [contacts, droughtZones, wells, allocationProfiles]);

  return (
    <Box>
      <Helmet title="Permits Management" />

      <Typography variant="h3" gutterBottom display="inline">
        Permits Management
      </Typography>

      <Breadcrumbs sx={{ mt: 4 }} aria-label="breadcrumb">
        <MuiLink component={RouterLink} to={ROUTES.LANDING}>
          Home
        </MuiLink>
        <Typography>Permits</Typography>
      </Breadcrumbs>

      <Divider sx={{ my: 6 }} />
      <DataGridInfoAlert title="Manage your permits:" />

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => setShowInactiveOnly((prev) => !prev)}
        >
          {showInactiveOnly ? "Show Active" : "Show Inactive"}
        </Button>
      </Box>

      <DataAdminGrid
        baseUrl={PERMITS_BASE_URL}
        columns={columns}
        primaryKeyField={PERMIT_PK_FIELD}
        allowAdd={!showInactiveOnly}
        rowFilter={(row) =>
          (showInactiveOnly
            ? row?.is_active === false
            : row?.is_active !== false) && rowMatchesSearch(row)
        }
      />
    </Box>
  );
}

export default PermitsPage;
